import { camelCase } from 'change-case';
import calculateSpecificity from 'css-specificity-calculator';
import defu from 'defu';
import styleUtils from './styleUtils';

export default class StyleReader {
  constructor(breakpoints = {}, states = [], properties = { fontFamily: [] }) {
    this.breakpoints = breakpoints;
    this.states = states;
    this.properties = properties;
    this.stylesheets = [];
    this.allClasses = {};
    this.initialised = {};
    Object.keys(this.properties).forEach(property => {
      this.initialiseProperty(this.initialised, property);
    });
  }

  getStyles(element, stylesheets) {
    const properties = JSON.parse(JSON.stringify(this.initialised));
    if (stylesheets !== this.stylesheets) {
      this.stylesheets = stylesheets;

      if( element )
        this.allClasses = this.getClasses(stylesheets, element.ownerDocument);

      this.allClasses = this.fitBreakpoints(this.allClasses);
    }
    Object.keys(this.allClasses).forEach(breakpointText => {
      const appliedClasses = this.getAppliedClasses(
        element,
        this.allClasses[breakpointText] || []
      );
      const breakpointProperties = this.getPropertiesFromClasses(
        appliedClasses,
        element,
        false
      );
      properties[breakpointText] = defu(
        breakpointProperties,
        properties[breakpointText]
      );
    });
    this.setInlineStyles(element, properties);
    
    return properties;
  }

  getAllElementStyles(body, stylesheets) {
    this.allClasses = this.getClasses(stylesheets, body.ownerDocument);
    this.allClasses = this.fitBreakpoints(this.allClasses);
    let parserId = 0;
    body.querySelectorAll('*').forEach(node => {
      node.setAttribute('data-gp-parser-id', parserId);
      parserId++;
    });
    const appliedClasses = {};
    for (const breakpointText of [
      'all',
      ...Object.values(this.getBreakpointLabelMap())
    ]) {
      if (!this.allClasses[breakpointText]) {
        this.allClasses[breakpointText] = [];
      }
      for (let i = 0; i < this.allClasses[breakpointText].length; i++) {
        const currentClass = this.allClasses[breakpointText][i];
        if (!currentClass.matchesRefNode && !['.hide-element'].includes(currentClass.selectorText) ) {
          let selector = currentClass.selectorText.replace(
            /(:hover|:active|:focus|.gp-hover|.gp-active|.gp-focus)$/g,
            ''
          );
          selector = selector ?
              selector.replace('[data-gp-component]', '') :
              currentClass.selectorText.replace('[data-gp-component]', '');
          body
            .querySelectorAll(selector)
            .forEach(node => {
              const nodeParserId = node.getAttribute('data-gp-parser-id');
              if (!appliedClasses[nodeParserId]) {
                appliedClasses[nodeParserId] = {};
              }
              if (!appliedClasses[nodeParserId][breakpointText]) {
                appliedClasses[nodeParserId][breakpointText] = [];
              }
              appliedClasses[nodeParserId][breakpointText].push(currentClass);
            });
        }
      }
    }
    return this.getAllElementProperties(body, appliedClasses);
  }

  getAllElementProperties(body, appliedClasses) {
    const stylemaps = [];
    for (const id in appliedClasses) {
      const element = body.querySelector(`[data-gp-parser-id="${id}"]`);
      const properties = {};
      for (const breakpointText of [
        'all',
        ...Object.values(this.getBreakpointLabelMap())
      ]) {
        if (!appliedClasses[id][breakpointText]) continue;
        const breakpointProperties = this.getPropertiesFromClasses(
          appliedClasses[id][breakpointText],
          element,
          true
        );
        properties[breakpointText] = breakpointProperties;
      }
      this.setInlineStyles(element, properties);
      stylemaps.push({ element, properties });
    }
    body.querySelectorAll(`[data-gp-parser-id]`).forEach(node => {
      node.removeAttribute('data-gp-parser-id');
    });
    return stylemaps;
  }

  getBreakpointLabelMap() {
    const breakpointValues = [...Object.values(this.breakpoints)]
      .map(breakpoint => parseInt(breakpoint.replace(/\D/g, ''), 10))
      .sort((a, b) => {
        return a - b;
      });
    const mapped = {};
    mapped[breakpointValues[0]] = `(max-width: ${breakpointValues[1] - 1}px)`;
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i < breakpointValues.length - 1; i++) {
      mapped[breakpointValues[i]] = `(max-width: ${breakpointValues[i + 1] -
        1}px) and (min-width: ${breakpointValues[i]}px)`;
    }
    mapped[breakpointValues[breakpointValues.length - 1]] = `(min-width: ${
      breakpointValues[breakpointValues.length - 1]
    }px)`;
    return mapped;
  }

  getBuilderBreakpoints() {
    return [...Object.values(this.breakpoints)]
      .map(breakpoint => parseInt(breakpoint.replace(/\D/g, ''), 10))
      .sort((a, b) => {
        return a - b;
      });
  }

  findClosestMinWidth(target) {
    const builderBreakpoints = this.getBuilderBreakpoints();
    let min = builderBreakpoints[1];
    [...builderBreakpoints].slice(1).forEach(builderBreakpoint => {
      if (builderBreakpoint <= target) {
        min = builderBreakpoint;
      }
    });
    return min;
  }

  findClosestMaxWidth(target) {
    const builderBreakpoints = this.getBuilderBreakpoints();
    const reversedBuilderBreakpoints = [...builderBreakpoints].reverse();
    let max = builderBreakpoints[0];
    if (target > reversedBuilderBreakpoints[0]) {
      max = reversedBuilderBreakpoints[0] - 1; // minus 1 to avoid overlapping with next min point while inserting classes in fitBreakpoints
    } else {
      reversedBuilderBreakpoints.forEach(builderBreakpoint => {
        if (builderBreakpoint >= target) {
          max = builderBreakpoint - 1;
        }
      });
    }
    return max;
  }

  fitBreakpoints(classes) {
    const builderBreakpoints = this.getBuilderBreakpoints();
    if (builderBreakpoints.length < 2) {
      return classes;
    }
    const contentBreakpoints = styleUtils.getContentBreakpoints(classes);
    const mergedBreakpoints = {};
    const labelMap = this.getBreakpointLabelMap();
    contentBreakpoints.forEach(breakpoint => {
      let smallest = builderBreakpoints[0];
      let biggest = builderBreakpoints[builderBreakpoints.length - 1];
      if (breakpoint.min) {
        smallest = this.findClosestMinWidth(breakpoint.min);
      }
      if (breakpoint.max) {
        biggest = this.findClosestMaxWidth(breakpoint.max);
      }
      builderBreakpoints.forEach(builderBreakpoint => {
        if (builderBreakpoint >= smallest && builderBreakpoint <= biggest) {
          mergedBreakpoints[labelMap[builderBreakpoint]] = [
            ...(mergedBreakpoints[labelMap[builderBreakpoint]] || []),
            ...classes[breakpoint.text]
          ];
        }
      });
    });
    mergedBreakpoints.all = classes.all || [];
    return mergedBreakpoints;
  }

  sortBreakpointClasses(classes) {
    if (classes.length < 2) return classes;
    const getMinPoint = condition => {
      const min = condition
        .split('(')
        .filter(selector => selector.includes('min-width'));
      return min.length ? parseInt(min[0].replace(/\D/g, ''), 10) : 0;
    };
    return classes.sort((currentClass, nextClass) => {
      const currentMin = currentClass.parentRule
        ? getMinPoint(currentClass.parentRule.conditionText)
        : 0;
      const nextMin = nextClass.parentRule
        ? getMinPoint(nextClass.parentRule.conditionText)
        : 0;
      if (currentMin > nextMin) return 1;
      if (currentMin < nextMin) return -1;
      return 0;
    });
  }

  setInlineStyles(element, properties) {
    [...element.style].forEach(style => {
      for (const breakpoint in properties) {
        if (properties[breakpoint].none) {
          properties[breakpoint].none[camelCase(style)] = element.style[style];
        }
      }
    });
  }

  initialiseProperty(values, property) {
    Object.values(this.getBreakpointLabelMap()).forEach(breakpointText => {
      // eslint-disable-next-line no-param-reassign
      if (!values[breakpointText]) values[breakpointText] = {};
      ['none', ...this.states].forEach(variant => {
        if (!values[breakpointText][variant])
          // eslint-disable-next-line no-param-reassign
          values[breakpointText][variant] = {};
        // eslint-disable-next-line no-param-reassign
        values[breakpointText][variant][property] = null;
      });
    });
  }

  getClasses(stylesheets, matchDocument) {
    const classes = {
      all: []
    };
    stylesheets
      .filter(
        sheet => !sheet.ownerNode.id.includes('fa-') // filter out fontawesome stylesheets
      )
      .forEach(sheet =>
        [...(sheet.cssRules || sheet.rules || [])].forEach(rule => {
          if (rule.selectorText) classes.all.push(rule);
          else if (rule.conditionText) {
            classes[rule.conditionText] = classes[rule.conditionText] || [];
            classes[rule.conditionText] = [
              ...classes[rule.conditionText],
              ...rule.cssRules
            ];
          }
        })
      );
    const refNode = matchDocument.createElement('custom-refnode');
    for (const breakpoint in classes) {
      classes[breakpoint] = classes[breakpoint]
        .map(classData => {
          let selectorText = classData.selectorText || '';
          this.states.forEach(variant => {
            selectorText = selectorText.replace(new RegExp(`:${variant}$`), '');
          });
          classData.parsedSelectorText = selectorText;
          if (selectorText && refNode.matches(selectorText)) {
            classData.matchesRefNode = true;
          }
          return classData;
        })
        .filter(classData => classData.parsedSelectorText);
    }
    return classes;
  }

  getClassnameFromSelector(selector) {
    return selector.replace('.', '').replace(new RegExp('\\\\', 'g'), '');
  }
  
  getAppliedClasses(el, classes) {
    let appliedClasses = [];
    for (let i = 0; i < classes.length; i++) {
      const parsedSelectorText = classes[i].parsedSelectorText.replace(
        /(:hover|:active|:focus|.gp-hover|.gp-active|.gp-focus)$/g,
        ''
      );
      if (
        (!classes[i].matchesRefNode ||
          el.tagName === 'BODY' ||
          el.tagName === 'BASE-REFNODE') &&
        el.matches(parsedSelectorText || classes[i].parsedSelectorText)
      ) {
        appliedClasses.push(classes[i]);
      }
    }
    appliedClasses = appliedClasses.filter(
      item => !item.parsedSelectorText.includes('builder-hidden')
    );
    return this.sortBreakpointClasses(appliedClasses);
  }

  getPropertiesFromClasses(classes, element, ignoreCloss) {
    const properties = {};
    classes.forEach(classData => {
      if( !classData.selectorText.includes('.gp-component-id') && !ignoreCloss ) return;
      const cssText = classData.style.cssText.split(';');
      const specicifity = this.getSpecicififty(classData.selectorText, element);
      let stateVariant = 'none';
      this.states.forEach(variant => {
        if (classData.cssText.includes(`:${variant.toLowerCase()}`))
          stateVariant = variant;
      });
      if (!properties[stateVariant]) {
        properties[stateVariant] = {};
      }
      for (let i = classData.style.length; i--; ) {
        const nameString = classData.style[i].includes('--')?classData.style[i]:camelCase(classData.style[i]);

        if (!properties[stateVariant][nameString]) properties[stateVariant][nameString] = [];

        let propertySpecicifity = specicifity;
        let propertyString = null;

        if( nameString.includes('--') ){
          let inlineStyle = {};
          
          for (const value of cssText) {
            const key = value.split(':')[0];
            const val = value.split(':')[1];

            if( key && val )
              inlineStyle[key.trim()] = val.trim();
          }          
          propertyString = inlineStyle[nameString];
        }else
          propertyString = classData.style[nameString];
  
        if(propertyString?.includes("rem"))
          propertyString = `${parseFloat(propertyString) * 16  }px`;
        
        if (
          cssText.find(
            string =>
              string
                .trim()
                .substr(0, classData.style[i].split('-')[0].trim().length) ===
                classData.style[i].split('-')[0].trim() &&
              string.includes('!important')
          )
        ) {
          propertySpecicifity = '10000';
          propertyString += ' !important';
        }
        properties[stateVariant][nameString].push({
          style: propertyString,
          specicifity: propertySpecicifity
        });
      }
    });
    for (const property in properties) {
      for (const variant in properties[property]) {
        properties[property][variant] = properties[property][variant].reduce(
          (max, current) => {
            if (max.specicifity > current.specicifity) {
              return max;
            }
            return current;
          }
        ).style;
      }
    }
    return properties;
  }

  getSpecicififty(selectorText, element) {
    const selectors = selectorText.split(',');
    const matching = [];
    selectors.forEach(selector => {
      if (selector.trim() && element.matches(selector)) {
        matching.push(selector);
      }
    });
    let specicifity = '';
    matching.forEach(selector => {
      const selectorSpecicifity = calculateSpecificity(selector.trim()).join(
        ''
      );
      if (selectorSpecicifity > specicifity) {
        specicifity = selectorSpecicifity;
      }
    });
    return specicifity;
  }
}
